import { http } from './http'
import { auth } from './auth'
import { z } from 'zod'
import {
  ClosingBookSchema,
  ClosingBookDocumentSetSchema,
  ClosingBookEventSchema,
  ClosingBookTemplateSchema,
  ClosingBookPreviewJobsSchema,
} from '../schemas'
import {
  ClosingBook,
  NewClosingBookData,
  ClosingBookDocumentSet,
  ClosingBookEvent,
  ClosingBookPreviewJobs,
  ClosingBookTemplate,
} from '../types'
import { serverVariables } from '../lib'
import { parseWithZod } from '../utils/parse-with-zod'

type PushCBToDMSPayload = {
  folder_id: string
  library_id: string
  workspace_id: number
}

type CBUpdateAccessPayload = {
  gas_is_public: boolean
  gas_group_ids: string[]
}

type SendBookPayload = {
  emails: string[]
  message: string
  subject: string // TODO: BE needs to add this field
}

const ClosingBookListSchema = z.object({
  closing_books: z.array(ClosingBookSchema),
})
const ClosingBookDocumentSetListSchema = z.object({
  closing_book_document_sets: z.array(ClosingBookDocumentSetSchema),
})
const ClosingBookEventListSchema = z.object({
  closing_book_events: z.array(ClosingBookEventSchema),
})
const ClosingBookTemplateListSchema = z.object({
  closing_book_templates: z.array(ClosingBookTemplateSchema),
})
const ClosingBookDocumentResponseSchema = z.object({
  closing_book_document_set: ClosingBookDocumentSetSchema,
})
export const ClosingBookResponseSchema = z.object({
  closing_book: ClosingBookSchema,
})

export const ClosingBookCreateResponseSchema = z.object({
  closing_book: z.object({
    closing_book_document_set_ids: z.array(z.number()),
    created_by_id: z.number().nullable(),
    date_created: z.string().nullable(),
    id: z.number(),
    num_files: z.number().nullable(),
    state: z.string(),
    state_updated_at: z.string(),
    deal_id: z.number(),
    description: z.string(),
    gas_group_ids: z.array(z.number()),
    gas_is_public: z.boolean(),
    is_final: z.boolean(),
    label: z.string(),
    template_id: z.number().nullable(),
  }),
})

export const closingBooks = {
  getAll: async (dealId: number): Promise<ClosingBook[]> => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/closing_books`)
    const parsedResult = parseWithZod(ClosingBookListSchema, result)
    return parsedResult.closing_books
  },

  getDocumentSets: async (dealId: number): Promise<ClosingBookDocumentSet[]> => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/closing_book_document_sets`)
    const parsedResult = parseWithZod(ClosingBookDocumentSetListSchema, result)
    return parsedResult.closing_book_document_sets
  },

  getEvents: async (dealId: number): Promise<ClosingBookEvent[]> => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/closing_book_events`)
    const parsedResult = parseWithZod(ClosingBookEventListSchema, result)
    return parsedResult.closing_book_events
  },

  getTemplates: async (): Promise<ClosingBookTemplate[]> => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/closing_book_templates`)
    const parsedResult = parseWithZod(ClosingBookTemplateListSchema, result)
    return parsedResult.closing_book_templates
  },

  generateClosingBookCoverPreview: async (
    dealId: number,
    payload: {
      title: string
      template_id: string
      closing_date: string | null
      blurb: string
    },
  ): Promise<ClosingBookPreviewJobs> => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/closing_book_cover_preview`,
      payload,
    )
    return parseWithZod(ClosingBookPreviewJobsSchema, result)
  },

  generateClosingBookIndexPreview: async (
    dealId: number,
    payload: {
      title: string
      template_id: string
      positions: Array<[number, string, number]>
      allow_number_resetting: boolean
      retain_agenda_numbering: boolean
      show_all_categories: boolean
      hide_all_categories: boolean
      blurb: string
      closing_date: string | null
    },
  ): Promise<ClosingBookPreviewJobs> => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/closing_book_index_preview`,
      payload,
    )
    return parseWithZod(ClosingBookPreviewJobsSchema, result)
  },

  createClosingBook: async (newClosingBook: NewClosingBookData) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/closing_books`,
      newClosingBook,
      Number(newClosingBook.closing_book.deal_id),
    )
    return parseWithZod(ClosingBookCreateResponseSchema, result)
  },

  sendBook: async (closingBookId: number, payload: SendBookPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return await http.post(`${regionHost}/api/closing_books/${closingBookId}/send`, payload, dealId)
  },

  pushToDMS: async (closingBookId: number, payload: PushCBToDMSPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/closing_books/${closingBookId}/push_to_dms`,
      payload,
      dealId,
    )
    const parsedResult = parseWithZod(ClosingBookDocumentResponseSchema, result)
    return parsedResult.closing_book_document_set
  },

  retryPushToDMS: async (documentSetId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/closing_book_document_sets/${documentSetId}/retry_push_to_dms`,
      undefined,
      serverVariables.dealId ?? undefined,
    )
    const parsedResult = parseWithZod(ClosingBookDocumentResponseSchema, result)
    return parsedResult.closing_book_document_set
  },

  updateAccess: async (id: number, payload: CBUpdateAccessPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/closing_books/${id}/update_access`,
      payload,
      dealId,
    )
    const parsedResult = parseWithZod(ClosingBookResponseSchema, result)
    return parsedResult.closing_book
  },

  deleteBook: async (bookId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return await http.delete(`${regionHost}/api/closing_books/${bookId}`, dealId)
  },
}
